import { LoadingSpinner } from "components/LoadingSpinner";
import { parsePrice } from "helpers/price";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { Paginator, PaginatorPageState } from "primereact/paginator";
import { Tooltip } from "primereact/tooltip";
import { Fragment, useEffect, useMemo, useState } from "react";
import {
  IContractModelingRow,
  useGetContractModelingAvailableCptCodesQuery,
  useGetContractModelingPayersWithDataQuery,
  usePostContractModelingAnalysisMutation,
} from "store/queries/contractModeling";
import { useGetAllPayersQuery } from "store/queries/payers";
import styled from "styled-components";

import { exportToCSV } from "../MarketPriceAnalysis/helpers";

const StyledContainer = styled.div`
  border-radius: 8px;
  border: 1px solid var(--gray-new-200);
  display: flex;
  flex-direction: column;
  width: 100%;

  .p-multiselect .p-multiselect-label {
    display: flex;
    align-items: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

function formatIsoDate(d: Date | null): string | undefined {
  if (!d) return undefined;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function formatDollars(value: number) {
  return parsePrice(value);
}

/** Backend sends comparison / primary as a ratio (e.g. 1.05). */
function formatRateRatioVsPrimary(ratio: number, primaryAvgPositive: boolean) {
  if (!primaryAvgPositive || ratio === 0) return "—";
  const pct = (ratio - 1) * 100;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(2)}%`;
}

function formatAvgRatioAsPercent(ratio: number) {
  if (ratio === 0) return "0.00%";
  const pct = (ratio - 1) * 100;
  const sign = pct > 0 ? "+" : "";
  return `${sign}${pct.toFixed(2)}%`;
}

function getMutationErrorMessage(err: unknown): string {
  if (!err || typeof err !== "object") return "Analysis request failed.";
  const anyErr = err as {
    data?: { message?: string };
    message?: string;
  };
  return anyErr.data?.message || anyErr.message || "Analysis request failed.";
}

export function ContractModelingAnalysis() {
  const [primaryPayerId, setPrimaryPayerId] = useState<number | null>(null);
  const [comparisonPayerIds, setComparisonPayerIds] = useState<number[]>([]);
  const [selectedCptCodes, setSelectedCptCodes] = useState<string[]>([]);
  const [serviceDateStart, setServiceDateStart] = useState<Date | null>(null);
  const [serviceDateEnd, setServiceDateEnd] = useState<Date | null>(null);
  const [first, setFirst] = useState(0);
  const [rows] = useState(10);
  const [isDownloading, setIsDownloading] = useState(false);

  const startDate = useMemo(
    () => formatIsoDate(serviceDateStart),
    [serviceDateStart]
  );
  const endDate = useMemo(
    () => formatIsoDate(serviceDateEnd),
    [serviceDateEnd]
  );

  const { data: payersData, isLoading: isLoadingPayersCatalog } =
    useGetAllPayersQuery();
  const { data: payerIdsWithData, isLoading: isLoadingPayersWithData } =
    useGetContractModelingPayersWithDataQuery();
  const { data: cptOptions = [], isLoading: isLoadingCpts } =
    useGetContractModelingAvailableCptCodesQuery({
      payerId: primaryPayerId ?? undefined,
      startDate,
      endDate,
    });

  const [
    postContractModelingAnalysis,
    {
      data: analysisData,
      isLoading: isLoadingAnalysis,
      error: analysisError,
      reset: resetAnalysis,
    },
  ] = usePostContractModelingAnalysisMutation();

  const payerOptions = useMemo(() => {
    if (!payersData?.data || !payerIdsWithData?.length) return [];
    const idSet = new Set(payerIdsWithData);
    return payersData.data
      .filter(
        (payer: { archivedAt?: string; name: string; id: number }) =>
          !payer.archivedAt &&
          payer.name !== "Clear Health" &&
          idSet.has(payer.id)
      )
      .map((payer: { name: string; id: number }) => ({
        label: payer.name,
        value: payer.id,
      }))
      .sort((a: { label: string }, b: { label: string }) =>
        a.label.localeCompare(b.label)
      );
  }, [payersData, payerIdsWithData]);

  const comparisonOptions = useMemo(() => {
    return payerOptions.filter(
      (o: { label: string; value: number }) => o.value !== primaryPayerId
    );
  }, [payerOptions, primaryPayerId]);

  const distinctComparisonIds = useMemo(() => {
    if (primaryPayerId == null) return [];
    return [...new Set(comparisonPayerIds)].filter(
      (id) => id !== primaryPayerId
    );
  }, [comparisonPayerIds, primaryPayerId]);

  const comparisonColumnOrder = useMemo(() => {
    if (!analysisData?.rows?.length) return [];
    return analysisData.rows[0].comparisonPayers.map((c) => c.payerId);
  }, [analysisData]);

  useEffect(() => {
    setComparisonPayerIds((prev) => prev.filter((id) => id !== primaryPayerId));
    setFirst(0);
    resetAnalysis();
  }, [primaryPayerId, resetAnalysis]);

  useEffect(() => {
    setFirst(0);
  }, [comparisonPayerIds, selectedCptCodes, startDate, endDate]);

  useEffect(() => {
    if (distinctComparisonIds.length === 0) {
      resetAnalysis();
    }
  }, [distinctComparisonIds.length, resetAnalysis]);

  useEffect(() => {
    if (!primaryPayerId || distinctComparisonIds.length === 0) {
      return;
    }

    postContractModelingAnalysis({
      primaryPayerId,
      comparisonPayerIds,
      cptCodes: selectedCptCodes.length > 0 ? [...selectedCptCodes] : undefined,
      startDate,
      endDate,
    });
  }, [
    primaryPayerId,
    comparisonPayerIds,
    selectedCptCodes,
    startDate,
    endDate,
    postContractModelingAnalysis,
    distinctComparisonIds.length,
  ]);

  const tableData = useMemo(() => {
    if (!analysisData?.rows?.length) return [];
    return analysisData.rows.slice(first, first + rows);
  }, [analysisData, first, rows]);

  const analysisSampleRow =
    analysisData && analysisData.rows.length > 0
      ? analysisData.rows[0]
      : undefined;

  const onPageChange = (event: PaginatorPageState) => {
    setFirst(event.first);
  };

  const handleDownloadData = async () => {
    if (!analysisData?.rows.length) return;
    try {
      setIsDownloading(true);
      const cOrder = analysisData.rows[0].comparisonPayers.map(
        (c) => c.payerId
      );
      const csvRows = analysisData.rows.map((row: IContractModelingRow) => {
        const base: Record<string, string | number> = {
          CPT: row.cptCode,
          Volume: row.volume,
          "Primary avg rate": formatDollars(row.primaryPayer.avgRate),
          "Primary revenue": formatDollars(row.primaryPayer.revenue),
        };
        cOrder.forEach((pid) => {
          const cell = row.comparisonPayers.find((c) => c.payerId === pid);
          const name = cell?.payerName ?? `Payer ${pid}`;
          base[`${name} avg rate`] = cell ? formatDollars(cell.avgRate) : "—";
          base[`${name} modeled revenue`] = cell
            ? formatDollars(cell.modeledRevenue)
            : "—";
          base[`${name} % vs primary`] = cell
            ? formatRateRatioVsPrimary(
                cell.rateChangePercent,
                row.primaryPayer.avgRate > 0
              )
            : "—";
        });
        base["Row revenue uplift (best comparison)"] = formatDollars(
          row.revenuePotential
        );
        return base;
      });
      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/:/g, "-");
      exportToCSV(csvRows, `contract_modeling_${timestamp}.csv`);
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoadingPayersCatalog || isLoadingPayersWithData) {
    return <LoadingSpinner />;
  }

  if (!payerIdsWithData?.length) {
    return (
      <StyledContainer className="bg-white">
        <div className="p-4">
          <div className="flex gap-2 align-items-center mb-3">
            <i className="pi pi-table text-2xl text-primary" />
            <p className="text-lg font-bold">Contract modeling</p>
          </div>
          <div className="text-center p-4 text-gray-500">
            No EDI claims with line items were found for this account, so
            contract modeling is not available yet.
          </div>
        </div>
      </StyledContainer>
    );
  }

  return (
    <StyledContainer className="bg-white">
      <Tooltip target=".contract-modeling-uplift-tooltip" />
      <div className="p-4">
        <div className="flex justify-content-between align-items-center mb-3">
          <div className="flex gap-2 align-items-center">
            <i className="pi pi-table text-2xl text-primary" />
            <p className="text-lg font-bold">Contract modeling</p>
          </div>
          {analysisData &&
            !isLoadingAnalysis &&
            analysisData.rows.length > 0 && (
              <Button
                label="Download"
                icon="pi pi-download"
                onClick={handleDownloadData}
                loading={isDownloading}
                className="p-button-outlined max-h-3rem max-w-12rem"
              />
            )}
        </div>

        <p className="text-sm text-gray-600 mb-3">
          Compare allowed amounts from 835 EDI claim lines: modeled revenue uses
          each comparison payer&apos;s historical average allowed per line
          applied to your primary payer&apos;s line volume.
        </p>

        <div className="flex flex-wrap gap-3 mb-4">
          <div className="flex flex-column gap-2" style={{ minWidth: "220px" }}>
            <label className="text-sm font-semibold">Primary payer</label>
            <Dropdown
              value={primaryPayerId}
              options={payerOptions}
              onChange={(e) => setPrimaryPayerId(e.value ?? null)}
              placeholder="Select primary payer"
              showClear
              filter
            />
          </div>

          <div
            className="flex flex-column gap-2 flex-grow-1"
            style={{ minWidth: "260px" }}
          >
            <label className="text-sm font-semibold">Comparison payers</label>
            <MultiSelect
              value={comparisonPayerIds}
              options={comparisonOptions}
              onChange={(e) => setComparisonPayerIds(e.value ?? [])}
              placeholder={
                !primaryPayerId
                  ? "Select a primary payer first"
                  : "Select one or more payers to compare"
              }
              filter
              showClear
              display="chip"
              className="w-full"
              disabled={!primaryPayerId}
            />
          </div>

          <div className="flex flex-column gap-2" style={{ minWidth: "150px" }}>
            <label className="text-sm font-semibold">Service date from</label>
            <Calendar
              value={serviceDateStart}
              onChange={(e) => setServiceDateStart(e.value as Date | null)}
              showIcon
              dateFormat="mm/dd/yy"
              placeholder="Optional"
              showButtonBar
            />
          </div>

          <div className="flex flex-column gap-2" style={{ minWidth: "150px" }}>
            <label className="text-sm font-semibold">Service date to</label>
            <Calendar
              value={serviceDateEnd}
              onChange={(e) => setServiceDateEnd(e.value as Date | null)}
              showIcon
              dateFormat="mm/dd/yy"
              placeholder="Optional"
              showButtonBar
            />
          </div>

          <div
            className="flex flex-column gap-2 flex-grow-1"
            style={{ minWidth: "240px" }}
          >
            <label className="text-sm font-semibold">
              CPT codes (optional)
            </label>
            <MultiSelect
              value={selectedCptCodes}
              options={cptOptions.map((c) => ({ label: c, value: c }))}
              onChange={(e) => setSelectedCptCodes(e.value ?? [])}
              placeholder={
                isLoadingCpts
                  ? "Loading CPT codes…"
                  : "All CPTs if none selected"
              }
              filter
              showClear
              display="chip"
              className="w-full"
              disabled={isLoadingCpts}
              emptyFilterMessage="No CPT codes match"
            />
          </div>
        </div>

        {analysisError && (
          <div className="p-3 mb-3 border-round border-1 border-red-200 bg-red-50 text-red-900 text-sm">
            {getMutationErrorMessage(analysisError)}
          </div>
        )}

        {isLoadingAnalysis && (
          <div className="flex justify-content-center p-5">
            <LoadingSpinner />
          </div>
        )}

        {primaryPayerId &&
          distinctComparisonIds.length > 0 &&
          analysisData &&
          !isLoadingAnalysis && (
            <div className="w-full">
              {analysisData.rows.length > 0 ? (
                <>
                  <DataTable
                    value={tableData}
                    stripedRows
                    showGridlines
                    responsiveLayout="scroll"
                    className="mb-3"
                  >
                    <Column
                      field="cptCode"
                      header="CPT"
                      style={{ width: "90px" }}
                    />
                    <Column
                      field="volume"
                      header="Volume (primary)"
                      body={(row: IContractModelingRow) => row.volume}
                      style={{ width: "120px" }}
                    />
                    <Column
                      header="Primary avg rate"
                      body={(row: IContractModelingRow) =>
                        formatDollars(row.primaryPayer.avgRate)
                      }
                      style={{ width: "120px" }}
                    />
                    <Column
                      header="Primary revenue"
                      body={(row: IContractModelingRow) =>
                        formatDollars(row.primaryPayer.revenue)
                      }
                      style={{ width: "130px" }}
                    />
                    {comparisonColumnOrder.map((payerId) => {
                      const sampleName =
                        analysisSampleRow?.comparisonPayers.find(
                          (c) => c.payerId === payerId
                        )?.payerName ?? `Payer ${payerId}`;
                      return (
                        <Fragment key={payerId}>
                          <Column
                            header={`${sampleName} avg rate`}
                            body={(row: IContractModelingRow) => {
                              const cell = row.comparisonPayers.find(
                                (c) => c.payerId === payerId
                              );
                              return cell ? formatDollars(cell.avgRate) : "—";
                            }}
                            style={{ width: "130px" }}
                          />
                          <Column
                            header={`${sampleName} modeled revenue`}
                            body={(row: IContractModelingRow) => {
                              const cell = row.comparisonPayers.find(
                                (c) => c.payerId === payerId
                              );
                              return cell
                                ? formatDollars(cell.modeledRevenue)
                                : "—";
                            }}
                            style={{ width: "150px" }}
                          />
                          <Column
                            header={`${sampleName} % vs primary`}
                            body={(row: IContractModelingRow) => {
                              const cell = row.comparisonPayers.find(
                                (c) => c.payerId === payerId
                              );
                              return cell
                                ? formatRateRatioVsPrimary(
                                    cell.rateChangePercent,
                                    row.primaryPayer.avgRate > 0
                                  )
                                : "—";
                            }}
                            style={{ width: "120px" }}
                          />
                        </Fragment>
                      );
                    })}
                    <Column
                      header={
                        <span
                          className="contract-modeling-uplift-tooltip font-semibold"
                          data-pr-tooltip="Per CPT, difference between primary revenue and the highest modeled revenue among selected comparison payers."
                          data-pr-position="top"
                        >
                          Row uplift (best comparison)
                        </span>
                      }
                      body={(row: IContractModelingRow) =>
                        formatDollars(row.revenuePotential)
                      }
                      style={{ width: "160px" }}
                    />
                  </DataTable>
                  {analysisData.rows.length > rows && (
                    <Paginator
                      first={first}
                      rows={rows}
                      totalRecords={analysisData.rows.length}
                      onPageChange={onPageChange}
                      className="mb-4"
                    />
                  )}

                  <div className="border-top-1 surface-border pt-3">
                    <p className="text-md font-bold mb-2">Summary</p>
                    <div className="flex flex-wrap gap-4 text-sm">
                      <div>
                        <div className="text-600">
                          Primary revenue (current)
                        </div>
                        <div className="text-xl font-semibold">
                          {formatDollars(
                            analysisData.totals.revenuePotential.current
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-600">
                          Modeled revenue (best comparison by CPT)
                        </div>
                        <div className="text-xl font-semibold">
                          {formatDollars(
                            analysisData.totals.revenuePotential.potential
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-600">Total uplift opportunity</div>
                        <div className="text-xl font-semibold text-primary">
                          {formatDollars(
                            analysisData.totals.revenuePotential.difference
                          )}
                        </div>
                      </div>
                      <div>
                        <div className="text-600">
                          Avg. rate vs primary (best comparison, CPT-weighted
                          mean)
                        </div>
                        <div className="text-xl font-semibold">
                          {formatAvgRatioAsPercent(
                            analysisData.totals.revenuePotential
                              .avgRateChangePercent
                          )}
                        </div>
                      </div>
                    </div>
                    {analysisData.totals.comparisonPayerRevenues.length > 0 && (
                      <div className="mt-3 text-sm">
                        <span className="font-semibold">
                          Total modeled revenue by payer:
                        </span>{" "}
                        {analysisData.totals.comparisonPayerRevenues
                          .map(
                            (t: {
                              payerName: string;
                              totalModeledRevenue: number;
                            }) =>
                              `${t.payerName}: ${formatDollars(
                                t.totalModeledRevenue
                              )}`
                          )
                          .join(" · ")}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="text-center p-4 text-gray-500">
                  No CPT rows matched the selected payers and filters.
                </div>
              )}
            </div>
          )}

        {!primaryPayerId && !isLoadingAnalysis && (
          <div className="text-center p-4 text-gray-500">
            Select a primary payer to run contract modeling.
          </div>
        )}

        {primaryPayerId &&
          distinctComparisonIds.length === 0 &&
          !isLoadingAnalysis && (
            <div className="text-center p-4 text-gray-500">
              Select at least one comparison payer other than the primary payer.
            </div>
          )}
      </div>
    </StyledContainer>
  );
}
